import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { editarJson, enviarJson, atualizarJson, pedirJson } from "./api";

/* Ativos (equipamentos, licenças…): o inventário que a equipe mantém e o cliente lê.
   Custo e observação da equipe só existem na resposta para o lado interno. O contrato é o de
   `api/app/dominios/ativos/`. */

export const CHAVE_DOS_ATIVOS = ["ativos"] as const;

const uuid = z.string().uuid();

export const CATEGORIAS_DE_ATIVO = [
  "computador",
  "servidor",
  "rede",
  "impressora",
  "celular",
  "software",
  "licenca",
  "outro",
] as const;
export const STATUS_DE_ATIVO = ["em_uso", "em_estoque", "em_manutencao", "baixado"] as const;

export type CategoriaDeAtivo = (typeof CATEGORIAS_DE_ATIVO)[number];
export type StatusDeAtivo = (typeof STATUS_DE_ATIVO)[number];

export const ROTULO_DA_CATEGORIA: Record<CategoriaDeAtivo, string> = {
  computador: "Computador",
  servidor: "Servidor",
  rede: "Rede",
  impressora: "Impressora",
  celular: "Celular",
  software: "Software",
  licenca: "Licença",
  outro: "Outro",
};

export const ROTULO_DO_STATUS: Record<StatusDeAtivo, string> = {
  em_uso: "Em uso",
  em_estoque: "Em estoque",
  em_manutencao: "Em manutenção",
  baixado: "Baixado",
};

const esquemaPessoa = z.object({ id: uuid, nome: z.string(), email: z.string() });

const esquemaAtivo = z.object({
  id: uuid,
  organizacao_id: uuid,
  organizacao_nome: z.string().nullable().optional(),
  nome: z.string(),
  categoria: z.enum(CATEGORIAS_DE_ATIVO),
  fabricante: z.string().nullable(),
  modelo: z.string().nullable(),
  numero_serie: z.string().nullable(),
  data_compra: z.string().nullable(),
  garantia_ate: z.string().nullable(),
  garantia_vencida: z.boolean(),
  status: z.enum(STATUS_DE_ATIVO),
  localizacao: z.string().nullable(),
  descricao: z.string().nullable(),
  pessoas: z.array(esquemaPessoa),
  total_de_chamados: z.number().int(),
  custo: z.string().nullable().optional(),
  observacao_interna: z.string().nullable().optional(),
  criado_em: z.string(),
  atualizado_em: z.string(),
});

const esquemaDetalheDoAtivo = esquemaAtivo.extend({
  chamados: z.array(
    z.object({ id: uuid, numero: z.number().int(), assunto: z.string(), estado: z.string() }),
  ),
});

const esquemaAtivoDoChamado = z.object({
  id: uuid,
  nome: z.string(),
  categoria: z.enum(CATEGORIAS_DE_ATIVO),
  numero_serie: z.string().nullable(),
  status: z.enum(STATUS_DE_ATIVO),
});

export type Ativo = z.infer<typeof esquemaAtivo>;
export type DetalheDoAtivo = z.infer<typeof esquemaDetalheDoAtivo>;
export type AtivoDoChamado = z.infer<typeof esquemaAtivoDoChamado>;

export interface FormularioDeAtivo {
  organizacao_id: string;
  nome: string;
  categoria: CategoriaDeAtivo;
  fabricante: string;
  modelo: string;
  numero_serie: string;
  data_compra: string;
  garantia_ate: string;
  status: StatusDeAtivo;
  localizacao: string;
  descricao: string;
  custo: string;
  observacao_interna: string;
}

export const ATIVO_EM_BRANCO: FormularioDeAtivo = {
  organizacao_id: "",
  nome: "",
  categoria: "computador",
  fabricante: "",
  modelo: "",
  numero_serie: "",
  data_compra: "",
  garantia_ate: "",
  status: "em_uso",
  localizacao: "",
  descricao: "",
  custo: "",
  observacao_interna: "",
};

export function formularioDoAtivo(ativo: Ativo): FormularioDeAtivo {
  return {
    organizacao_id: ativo.organizacao_id,
    nome: ativo.nome,
    categoria: ativo.categoria,
    fabricante: ativo.fabricante ?? "",
    modelo: ativo.modelo ?? "",
    numero_serie: ativo.numero_serie ?? "",
    data_compra: ativo.data_compra ?? "",
    garantia_ate: ativo.garantia_ate ?? "",
    status: ativo.status,
    localizacao: ativo.localizacao ?? "",
    descricao: ativo.descricao ?? "",
    custo: ativo.custo ?? "",
    observacao_interna: ativo.observacao_interna ?? "",
  };
}

/** Validação antes de enviar; a API repete a conferência. */
export function validarAtivo(form: FormularioDeAtivo, novo: boolean): string | null {
  if (novo && !form.organizacao_id) return "Escolha a organização do ativo.";
  if (form.nome.trim().length < 1) return "Dê um nome ao ativo.";
  if (form.data_compra && form.garantia_ate && form.garantia_ate < form.data_compra) {
    return "A garantia não pode vencer antes da data da compra.";
  }
  if (form.custo.trim()) {
    const valor = Number(form.custo.replace(",", "."));
    if (!Number.isFinite(valor) || valor < 0) return "O custo precisa ser um valor maior ou igual a zero.";
  }
  return null;
}

/** O corpo da API: texto vazio vira `null`; o custo aceita vírgula. `incluirInterno` = lado da equipe. */
export function corpoDoAtivo(
  form: FormularioDeAtivo,
  opcoes: { novo: boolean; incluirInterno: boolean },
): Record<string, unknown> {
  const texto = (valor: string) => (valor.trim() ? valor.trim() : null);
  const corpo: Record<string, unknown> = {
    nome: form.nome.trim(),
    categoria: form.categoria,
    fabricante: texto(form.fabricante),
    modelo: texto(form.modelo),
    numero_serie: texto(form.numero_serie),
    data_compra: texto(form.data_compra),
    garantia_ate: texto(form.garantia_ate),
    status: form.status,
    localizacao: texto(form.localizacao),
    descricao: texto(form.descricao),
  };
  if (opcoes.novo) corpo.organizacao_id = form.organizacao_id;
  if (opcoes.incluirInterno) {
    corpo.custo = form.custo.trim() ? Number(form.custo.replace(",", ".")).toFixed(2) : null;
    corpo.observacao_interna = texto(form.observacao_interna);
  }
  return corpo;
}

export function dataLegivel(valor: string | null): string {
  if (!valor) return "—";
  const [ano, mes, dia] = valor.split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
}

export function custoLegivel(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor));
}

export function estadoDoChipDoStatus(status: StatusDeAtivo): "ok" | "atencao" | "neutro" | "erro" {
  return { em_uso: "ok", em_estoque: "neutro", em_manutencao: "atencao", baixado: "erro" }[status] as
    | "ok"
    | "atencao"
    | "neutro"
    | "erro";
}

/* ------------------------------------------------------------------------------- consultas */

export interface FiltrosDeAtivos {
  organizacaoId?: string;
  busca?: string;
  categoria?: string;
  status?: string;
  minhas?: boolean;
}

export function useAtivos(filtros: FiltrosDeAtivos = {}, habilitado = true) {
  const parametros = new URLSearchParams({ limite: "500" });
  if (filtros.organizacaoId) parametros.set("organizacao_id", filtros.organizacaoId);
  if (filtros.busca?.trim()) parametros.set("busca", filtros.busca.trim());
  if (filtros.categoria) parametros.set("categoria", filtros.categoria);
  if (filtros.status) parametros.set("status", filtros.status);
  if (filtros.minhas) parametros.set("minhas", "true");
  return useQuery({
    queryKey: [...CHAVE_DOS_ATIVOS, "lista", parametros.toString()],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(`/ativos?${parametros.toString()}`, z.array(esquemaAtivo), { signal }),
  });
}

export function useAtivo(id: string | null) {
  return useQuery({
    queryKey: [...CHAVE_DOS_ATIVOS, "detalhe", id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      pedirJson(`/ativos/${encodeURIComponent(id ?? "")}`, esquemaDetalheDoAtivo, { signal }),
  });
}

export function useSalvarAtivo() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id?: string; corpo: Record<string, unknown> }) =>
      id
        ? editarJson(`/ativos/${encodeURIComponent(id)}`, esquemaDetalheDoAtivo, corpo)
        : enviarJson("/ativos", esquemaDetalheDoAtivo, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_ATIVOS }),
  });
}

export function useDefinirPessoasDoAtivo() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, usuarioIds }: { id: string; usuarioIds: string[] }) =>
      atualizarJson(`/ativos/${encodeURIComponent(id)}/pessoas`, esquemaDetalheDoAtivo, {
        usuario_ids: usuarioIds,
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DOS_ATIVOS }),
  });
}

export function useAtivosDoChamado(ticketId: string, habilitado: boolean) {
  return useQuery({
    queryKey: [...CHAVE_DOS_ATIVOS, "chamado", ticketId],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(
        `/tickets/${encodeURIComponent(ticketId)}/ativos`,
        z.array(esquemaAtivoDoChamado),
        { signal },
      ),
  });
}

export function useDefinirAtivosDoChamado(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (ativoIds: string[]) =>
      atualizarJson(`/tickets/${encodeURIComponent(ticketId)}/ativos`, z.array(esquemaAtivoDoChamado), {
        ativo_ids: ativoIds,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DOS_ATIVOS });
      void cliente.invalidateQueries({ queryKey: ["tickets", ticketId] });
    },
  });
}
